import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as PhosphorIcons from "@phosphor-icons/react";
import { Truck, X, Sparkle, ArrowRight, Eye, EyeSlash, PushPin, PushPinSlash, ArrowsOutSimple, ArrowsInSimple, ArrowsClockwise, Warning, CheckCircle, Info, XCircle, Lightning, ListChecks, GearSix } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import "../styles/launch.css";

/**
 * Roll Fast TMS — Installed-App First-Launch Experience.
 *
 * Triggers automatically when:
 *   • The app is running in standalone display mode (installed PWA), AND
 *   • The user has never seen the current LAUNCH_VERSION
 * Can also be triggered manually via the "Show What's New" button anywhere
 * in the admin shell (set window.dispatchEvent(new Event('rf:show-launch'))).
 *
 * Everything is data-driven from `GET /api/onboarding/launch`. No tile/role/
 * widget content is hardcoded in this component — it's all server config.
 */
const LS_DISMISSED = "rf_launch_dismissed_v";

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
};
const inIframe = () => {
  try { return window.self !== window.top; } catch { return true; }
};

const SEV_META = {
  critical: { label: "Critical", Icon: XCircle, cls: "alert-critical", color: "#DC2626" },
  warning:  { label: "Warning",  Icon: Warning,  cls: "alert-warning",  color: "#CA8A04" },
  info:     { label: "Info",     Icon: Info,     cls: "alert-info",     color: "#2563EB" },
  success:  { label: "On Track", Icon: CheckCircle, cls: "alert-success", color: "#16A34A" },
};

function getIcon(name) {
  // Phosphor exports PascalCase. Falls back to Sparkle.
  const C = PhosphorIcons[name];
  return C || Sparkle;
}

export default function LaunchExperience() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [compact, setCompact] = useState(false);
  const fetchedFor = useRef(null);

  /* Decide whether to auto-open on mount. */
  useEffect(() => {
    if (!user) return;
    // Always allow a manual trigger via window event regardless of conditions.
    const handler = () => setOpen(true);
    window.addEventListener("rf:show-launch", handler);

    // Auto-trigger only inside installed PWA standalone mode, never inside the
    // Emergent preview iframe (which would annoy the dev/admin every reload).
    if (!isStandalone() || inIframe()) return () => window.removeEventListener("rf:show-launch", handler);

    // Already dismissed this version on this device?
    const lastSeen = localStorage.getItem(LS_DISMISSED);
    // We fetch once to know the current LAUNCH_VERSION from the server; if the
    // server reports `should_show=true` then we open. This keeps the source of
    // truth on the server (admin can bump LAUNCH_VERSION to force everyone).
    (async () => {
      try {
        const { data: d } = await api.get("/onboarding/launch");
        if (d?.should_show && lastSeen !== d.launch_version) {
          setOpen(true);
          setData(d);
          fetchedFor.current = d.launch_version;
        }
      } catch { /* silent — onboarding is an enhancement, not critical-path */ }
    })();

    return () => window.removeEventListener("rf:show-launch", handler);
  }, [user]);

  /* Fetch data when the dialog actually opens (and not already populated). */
  useEffect(() => {
    if (!open) return;
    if (data && fetchedFor.current) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get("/onboarding/launch");
        if (!alive) return;
        setData(d);
        setCompact(!!d?.prefs?.compact);
        fetchedFor.current = d?.launch_version;
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, data]);

  /* ESC closes, body scroll lock. */
  useEffect(() => {
    if (!open) return;
    document.body.classList.add("rf-no-scroll");
    const onKey = (e) => { if (e.key === "Escape") close(false); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("rf-no-scroll");
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const close = useCallback(async (markSeen) => {
    if (markSeen && data?.launch_version) {
      try { await api.post("/onboarding/dismiss"); } catch { /* no-op */ }
      try { localStorage.setItem(LS_DISMISSED, data.launch_version); } catch { /* no-op */ }
    }
    setOpen(false);
    setEditing(false);
  }, [data]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/onboarding/launch");
      setData(d);
      setCompact(!!d?.prefs?.compact);
    } finally {
      setLoading(false);
    }
  }, []);

  const tiles = data?.tiles || [];
  const alerts = data?.alerts || [];
  const brief = data?.morning_brief;
  const config = data?.config || {};
  const prefs = data?.prefs || { hidden_tile_ids: [], ordered_tile_ids: [], pinned_tile_ids: [], compact: false };

  const allTiles = useMemo(() => tiles, [tiles]);
  const hiddenSet = useMemo(() => new Set(prefs.hidden_tile_ids), [prefs.hidden_tile_ids]);
  const pinnedSet = useMemo(() => new Set(prefs.pinned_tile_ids), [prefs.pinned_tile_ids]);

  const savePrefs = useCallback(async (patch) => {
    const next = {
      hidden_tile_ids: prefs.hidden_tile_ids,
      ordered_tile_ids: prefs.ordered_tile_ids,
      pinned_tile_ids: prefs.pinned_tile_ids,
      compact: compact,
      ...patch,
    };
    setData((d) => d ? { ...d, prefs: { ...d.prefs, ...next } } : d);
    try { await api.post("/onboarding/prefs", next); } catch { /* silent — UI already updated */ }
  }, [prefs, compact]);

  const togglePin = (id) => {
    const set = new Set(prefs.pinned_tile_ids);
    set.has(id) ? set.delete(id) : set.add(id);
    savePrefs({ pinned_tile_ids: Array.from(set) });
  };
  const toggleHide = (id) => {
    const set = new Set(prefs.hidden_tile_ids);
    set.has(id) ? set.delete(id) : set.add(id);
    savePrefs({ hidden_tile_ids: Array.from(set) });
    setTimeout(refresh, 200); // re-fetch counts on the new visible set
  };
  const toggleCompact = () => {
    setCompact((c) => { const n = !c; savePrefs({ compact: n }); return n; });
  };

  const onTileClick = (tile) => {
    if (editing) return;
    close(true);
    if (tile.route) nav(tile.route);
  };

  if (!open) return null;

  return (
    <div className="launch-overlay" data-testid="launch-experience" role="dialog" aria-label="Roll Fast launch experience">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-24">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-6 sm:mb-8 launch-card" style={{ animationDelay: "0ms" }}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center rounded shrink-0" style={{ background: "#E50914" }}>
              <Truck size={22} weight="fill" color="#fff" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">Roll Fast TMS</div>
              <div className="font-display font-black text-xl sm:text-2xl truncate" style={{ color: "#fff" }}>Welcome back</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setEditing((e) => !e)}
              className="rf-btn rf-btn-ghost text-xs"
              data-testid="launch-edit-btn"
              title={editing ? "Done editing" : "Personalize"}
            >
              {editing ? <CheckCircle size={14} weight="bold" /> : <GearSix size={14} weight="bold" />}
              <span className="hidden sm:inline ml-1">{editing ? "Done" : "Edit"}</span>
            </button>
            <button
              type="button"
              onClick={toggleCompact}
              className="rf-btn rf-btn-ghost text-xs"
              title={compact ? "Expanded view" : "Compact view"}
              data-testid="launch-compact-btn"
            >
              {compact ? <ArrowsOutSimple size={14} weight="bold" /> : <ArrowsInSimple size={14} weight="bold" />}
            </button>
            <button
              type="button"
              onClick={refresh}
              className="rf-btn rf-btn-ghost text-xs"
              disabled={loading}
              title="Refresh"
              data-testid="launch-refresh-btn"
            >
              <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
            </button>
            <button
              type="button"
              onClick={() => close(true)}
              className="rf-btn rf-btn-ghost text-xs"
              data-testid="launch-close-btn"
              aria-label="Close"
            >
              <X size={14} weight="bold" />
            </button>
          </div>
        </div>

        {/* Greeting */}
        <div className="mb-6 sm:mb-8 launch-card" style={{ animationDelay: "60ms" }}>
          <h1 className="launch-greeting font-display font-black text-3xl sm:text-5xl tracking-tight leading-tight">
            {data?.greeting || "Hello"}.
          </h1>
          <p className="text-neutral-400 mt-2 text-sm sm:text-base">
            {brief ? "Here's what needs attention today." : "Jump straight to where you left off."}
          </p>
        </div>

        {/* Morning Brief + Alerts (two-col on lg) */}
        <div className={`grid gap-4 ${brief && alerts.length ? "lg:grid-cols-3" : "grid-cols-1"}`}>
          {brief && config.show_morning_brief && (
            <div className={`launch-glass launch-card rounded-lg p-4 sm:p-5 ${alerts.length ? "lg:col-span-2" : ""}`} style={{ animationDelay: "120ms" }} data-testid="launch-morning-brief">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <Sparkle size={16} weight="fill" color="#E50914" />
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">
                    AI Operations Brief
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {brief.health && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-widest"
                      style={{
                        background: "rgba(255,255,255,0.06)",
                        color: brief.health.band === "critical" ? "#DC2626" : brief.health.band === "stressed" ? "#CA8A04" : "#16A34A",
                        border: `1px solid ${(brief.health.band === "critical" ? "#DC2626" : brief.health.band === "stressed" ? "#CA8A04" : "#16A34A")}40`,
                      }}
                      data-testid="launch-health-badge"
                      title={brief.delta?.trend === "improving" ? "Improving" : brief.delta?.trend === "declining" ? "Declining" : "Stable"}
                    >
                      Health {brief.health.score}
                      {brief.delta?.trend === "improving" && <span>↑</span>}
                      {brief.delta?.trend === "declining" && <span>↓</span>}
                    </span>
                  )}
                  <div className="text-[9px] uppercase tracking-widest text-neutral-600 font-mono" title={brief.source}>
                    {brief.source === "claude-sonnet-4-5" ? "Claude · 4.5" : brief.source === "empty-state" ? "Quiet" : "Heuristic"}
                  </div>
                </div>
              </div>
              {brief.narrative ? (
                <p className="text-sm sm:text-base text-neutral-100 leading-relaxed whitespace-pre-line" data-testid="launch-brief-narrative">
                  {brief.narrative}
                </p>
              ) : brief.bullets?.length ? (
                <ul className="space-y-1.5 text-sm text-neutral-200">
                  {brief.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="inline-block w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: "#E50914" }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-neutral-500">Nothing urgent. All systems normal.</div>
              )}

              {brief.summary_bullets?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="launch-brief-chips">
                  {brief.summary_bullets.map((b, i) => (
                    <span key={i} className="text-[10px] uppercase tracking-widest font-mono px-2 py-1 rounded" style={{ background: "rgba(229, 9, 20, 0.10)", color: "#E5E5E5", border: "1px solid rgba(229, 9, 20, 0.25)" }}>
                      {b}
                    </span>
                  ))}
                </div>
              )}

              {brief.primary_action && (
                <button
                  type="button"
                  onClick={() => { close(true); nav(brief.primary_action.route); }}
                  className="mt-4 w-full launch-tile rounded p-3 flex items-start gap-3 text-left"
                  data-testid="launch-brief-cta"
                >
                  <div className="shrink-0 w-8 h-8 rounded flex items-center justify-center" style={{ background: "#E50914" }}>
                    <Lightning size={16} weight="fill" color="#fff" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">Recommended first action</div>
                    <div className="text-sm font-semibold text-white">{brief.primary_action.label}</div>
                    {brief.primary_action.reason && (
                      <div className="text-[11px] text-neutral-400 mt-0.5">{brief.primary_action.reason}</div>
                    )}
                  </div>
                  <ArrowRight size={16} weight="bold" className="shrink-0 mt-1.5 text-neutral-400" />
                </button>
              )}

              {brief.actions?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="launch-brief-actions">
                  {brief.actions.map((a, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => { close(true); nav(a.route); }}
                      className="rf-btn rf-btn-ghost text-xs"
                      data-testid={`launch-brief-action-${i}`}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {!!alerts.length && config.show_alerts && (
            <div className="launch-glass launch-card rounded-lg p-4 sm:p-5" style={{ animationDelay: "180ms" }} data-testid="launch-alerts">
              <div className="flex items-center gap-2 mb-3">
                <ListChecks size={16} weight="fill" color="#fff" />
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">Smart Alerts</div>
              </div>
              <div className="space-y-2">
                {alerts.map((a, i) => {
                  const meta = SEV_META[a.severity] || SEV_META.info;
                  const Icon = meta.Icon;
                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={!a.route}
                      onClick={() => { if (a.route) { close(true); nav(a.route); } }}
                      className={`${meta.cls} w-full rounded px-3 py-2 text-left flex items-start gap-2 disabled:cursor-default`}
                      data-testid={`launch-alert-${a.severity}`}
                    >
                      <Icon size={14} weight="fill" color={meta.color} className="shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold text-white">{a.title}</div>
                        <div className="text-[11px] text-neutral-300 leading-snug">{a.body}</div>
                      </div>
                      {a.route && <ArrowRight size={12} weight="bold" className="shrink-0 mt-1 text-neutral-500" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Shortcut Tiles */}
        <div className="mt-6 sm:mt-8">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">Jump straight in</div>
            {editing && (
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">Pin · Hide · Reorder</div>
            )}
          </div>
          {loading && tiles.length === 0 ? (
            <div className={`grid gap-3 ${compact ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}>
              {[1,2,3,4,5,6].map((i) => (
                <div key={i} className="launch-tile rounded-lg p-4 animate-pulse h-28" />
              ))}
            </div>
          ) : (
            <div className={`grid gap-3 ${compact ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}>
              {allTiles.map((tile, idx) => {
                const Icon = getIcon(tile.icon);
                const isPinned = pinnedSet.has(tile.id);
                const isHidden = hiddenSet.has(tile.id);
                const cnt = tile.count || {};
                const statusColor = cnt.status === "critical" ? "#DC2626"
                                  : cnt.status === "warning"  ? "#CA8A04"
                                  : cnt.status === "info"     ? "#2563EB"
                                  : cnt.status === "success" || cnt.status === "ok" ? "#16A34A"
                                  : "#525252";
                return (
                  <button
                    type="button"
                    key={tile.id}
                    onClick={() => onTileClick(tile)}
                    className={`launch-tile launch-card rounded-lg text-left ${isPinned ? "pinned" : ""} ${isHidden ? "opacity-50" : ""} ${compact ? "p-3" : "p-4 sm:p-5"}`}
                    style={{ animationDelay: `${240 + idx * 50}ms` }}
                    data-testid={`launch-tile-${tile.id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="w-9 h-9 rounded flex items-center justify-center shrink-0" style={{ background: "rgba(229, 9, 20, 0.12)", color: "#E50914" }}>
                        <Icon size={18} weight="bold" />
                      </div>
                      {editing ? (
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); togglePin(tile.id); }}
                            className="text-neutral-500 hover:text-white p-1"
                            aria-label="Toggle pin"
                          >
                            {isPinned ? <PushPinSlash size={14} weight="bold" /> : <PushPin size={14} weight="bold" />}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleHide(tile.id); }}
                            className="text-neutral-500 hover:text-white p-1"
                            aria-label="Toggle hide"
                          >
                            {isHidden ? <Eye size={14} weight="bold" /> : <EyeSlash size={14} weight="bold" />}
                          </button>
                        </div>
                      ) : (
                        cnt.status && cnt.status !== "ok" && (
                          <span className="inline-block w-2 h-2 rounded-full" style={{ background: statusColor }} aria-label={cnt.status} />
                        )
                      )}
                    </div>
                    <div className={`font-bold text-white ${compact ? "text-sm" : "text-base"}`}>{tile.label}</div>
                    {!compact && tile.description && (
                      <div className="text-xs text-neutral-500 mt-0.5">{tile.description}</div>
                    )}
                    {cnt.value !== null && cnt.value !== undefined && (
                      <div className="mt-3 flex items-baseline gap-2">
                        <span className={`font-display font-black ${compact ? "text-xl" : "text-2xl"}`} style={{ color: statusColor }}>
                          {cnt.value}
                        </span>
                        {cnt.label && !compact && (
                          <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">{cnt.label}</span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
              {allTiles.length === 0 && (
                <div className="col-span-full text-center py-12 text-neutral-500 text-sm">
                  No shortcuts configured for your role yet. An admin can set them up in <em>/admin/onboarding-config</em>.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-3 launch-card" style={{ animationDelay: "600ms" }}>
          <div className="text-[10px] uppercase tracking-widest text-neutral-600 font-mono">
            Roll Fast Solutions LLC · v{data?.launch_version || "—"}
          </div>
          <button
            type="button"
            onClick={() => close(true)}
            className="rf-btn rf-btn-primary"
            data-testid="launch-start-btn"
          >
            Start working <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
